import { useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { checkAndSendCriticalAlert } from '@/utils/criticalAlertSystem';

export interface CriticalError {
  id?: string;
  user_id?: string;
  error_type: 'auth' | 'payment' | 'api' | 'database' | 'edge_function' | 'general';
  error_message: string;
  error_stack?: string;
  component?: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  metadata?: Record<string, any>;
  created_at?: string;
}

export const useErrorMonitoring = () => {
  const { user } = useAuth();

  const logError = async (error: Omit<CriticalError, 'id' | 'created_at'>) => {
    try {
      const { error: dbError } = await supabase
        .from('error_logs' as any)
        .insert({
          user_id: error.user_id || user?.id,
          error_type: error.error_type,
          error_message: error.error_message,
          error_stack: error.error_stack,
          component: error.component,
          severity: error.severity,
          metadata: error.metadata,
        });

      if (dbError) {
        console.error('Failed to log error:', dbError);
      }

      // Verificar se deve enviar alerta crítico
      await checkAndSendCriticalAlert(
        error.error_type,
        error.error_message,
        error.severity,
        error.component
      );
    } catch (err) {
      console.error('Error monitoring failed:', err);
    }
  };

  useEffect(() => {
    // Capturar erros não tratados globalmente
    const handleError = (event: ErrorEvent) => {
      logError({
        error_type: 'general',
        error_message: event.message,
        error_stack: event.error?.stack,
        component: 'global',
        severity: 'high',
        metadata: {
          filename: event.filename,
          lineno: event.lineno,
          colno: event.colno,
        },
      });
    };

    // Capturar promise rejections não tratadas
    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      logError({
        error_type: 'general',
        error_message: event.reason?.message || 'Unhandled Promise Rejection',
        error_stack: event.reason?.stack,
        component: 'promise',
        severity: 'high',
        metadata: {
          reason: event.reason,
        },
      });
    };

    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, [user]);

  return { logError };
};

// Helper function para usar fora de componentes React
export const logCriticalError = async (error: Omit<CriticalError, 'id' | 'created_at' | 'user_id'>) => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    
    await supabase.from('error_logs' as any).insert({
      user_id: user?.id,
      error_type: error.error_type,
      error_message: error.error_message,
      error_stack: error.error_stack,
      component: error.component,
      severity: error.severity,
      metadata: error.metadata,
    });
  } catch (err) {
    console.error('Failed to log critical error:', err);
  }
};
